import React from "react";
import { Link } from "react-router-dom";
import TableCell from "@material-ui/core/TableCell";
import numberFormatter from "utils/numberFormatter";
import LoanTableOptionsMenu from "components/Global/OptionsMenu/LoanTable/LoanTableOptionsMenu";
import _ from "lodash";
import hedgeTypes from "constants/hedgeTypes";

import { calculateClosingCosts } from "./util";

const RejectedLoanTableColumns = [
  {
    id: "b62a77fa-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: true,
    dataField: "lenderCompany.name",
    numeric: false,
    label: "Lender",
    Cell: ({ record, callbacks }) => (
      <TableCell>
        {(
          <Link to={`/loan/${callbacks.getDealId()}/${record.id}`}>
            {record["lenderCompany.name"] || "Lender Not Selected"}
          </Link>
        ) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "ba2b5232-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amount",
    numeric: false,
    label: "Total Loan Amount",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amount) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "bed13efb-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "initialProceeds",
    numeric: false,
    label: "Initial Proceeds",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.initialProceeds) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "c1e6ae9e-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.ltvAsIs",
    numeric: false,
    label: "LTV",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["covenants.ltvStabilized"], 2, "\u2014") || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "d1c7e040-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanTermMonths",
    numeric: false,
    label: "Term",
    Cell: ({ record }) => (
      <TableCell>{record.loanTermMonths || "\u2014"}</TableCell>
    )
  },
  {
    id: "d48f4d35-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "extensionTerms.extensionTermMonths",
    numeric: false,
    label: "Extensions",
    Cell: ({ record }) => {
      const extensionOptions = _.get(record, "extensionTerms.extensionOptions");
      const extensionTermMonths = _.get(record, "extensionTerms.extensionTermMonths");
      if (!extensionOptions || !extensionTermMonths) {
        return <TableCell>{"\u2014"}</TableCell>;
      }

      return (
        <TableCell>{`${extensionOptions}, ${extensionTermMonths} month extensions`}</TableCell>
      );
    }
  },
  {
    id: "debe9caa-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanPricing",
    numeric: false,
    label: "Loan Pricing",
    Cell: ({ record }) => <TableCell>{ record.loanPricing ? record.loanPricing : "\u2014"}</TableCell>
  },
  {
    id: "e0d921b9-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "currentRate",
    numeric: false,
    label: "Current Rate",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record.currentRate, 2, "\u2014") }
      </TableCell>
    )
  },
  {
    id: "e384d23f-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amortizationTerm",
    numeric: false,
    label: "Amortization Term",
    Cell: ({ record }) => (
      <TableCell>{record.amortizationTerm || "\u2014"}</TableCell>
    )
  },
  {
    id: "e6365792-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "ioPeriod",
    numeric: false,
    label: "I/O Term",
    Cell: ({ record }) => <TableCell>{record.ioPeriod || "\u2014"}</TableCell>
  },
  {
    id: "e923dd24-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "interestOnlyPayment",
    numeric: false,
    label: "I/O Payment",
    Cell: ({ record }) => <TableCell>{ numberFormatter.maskCurrency(record.interestOnlyPayment) || "\u2014"}</TableCell>
  },
  {
    id: "ec4495b4-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "amortizingPayment",
    numeric: false,
    label: "Amortizing Payment",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.maskCurrency(record.amortizingPayment) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "eed906b8-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.hedgeTypeId",
    numeric: false,
    label: "Hedge Type",
    Cell: ({ record }) => {
      if (!_.get(record, "prefHedge.hedgeTypeId")) {
        return <TableCell>{"\u2014"}</TableCell>;
      }

      return (
        <TableCell>
          {
            hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0] ?
              hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0].name : "Unknown"
          }
        </TableCell>
      );
    }
  },
  {
    id: "f1d63559-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.marketCost",
    numeric: false,
    label: "Hedge Premium",
    Cell: ({ record }) => {
      const hedgeType = _.get(record, "prefHedge.hedgeTypeId", "\u2014");
      if (hedgeType === hedgeTypes.CAP) {
        return (<TableCell>{numberFormatter.maskCurrency(_.get(record, "prefHedge.marketCost"), "\u2014", true)}</TableCell>);
      } else if (hedgeType === hedgeTypes.SWAP) {
        return (<TableCell>{numberFormatter.formatRate(_.get(record, "prefHedge.marketRate"), 2, "\u2014") }</TableCell>);
      }

      return (<TableCell>{"\u2014"}</TableCell>);
    }
  },
  {
    id: "f43eccd0-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "closingCosts.totalAmount",
    numeric: false,
    label: "Closing Costs",
    Cell: ({ record }) => <TableCell>{numberFormatter.maskCurrency(_.get(record, "closingCosts.totalAmount", "\u2014"))}</TableCell>
  },
  {
    id: "f7378118-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanRecourse.recourseType.name",
    numeric: false,
    label: "Recourse",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "loanRecourse.recourseType.name", "\u2014")}</TableCell>
    )
  },
  {
    id: "f9f2defe-4a49-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prepaymentTerms.prepaymentType.value",
    numeric: false,
    label: "Prepayment Type",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "prepaymentTerms.prepaymentType.value") || "\u2014"}</TableCell>
    )
  },
  {
    id: "041783c5-4a4a-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.dscrStabilized",
    numeric: false,
    label: "DSCR",
    Cell: ({ record }) => (
      <TableCell>{record["covenants.dscrStabilized"] ? `${record["covenants.dscrStabilized"]}x` : "\u2014"}</TableCell>
    )
  },
  {
    id: "06cf1652-4a4a-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.debtYieldStabilized",
    numeric: false,
    label: "Debt Yield",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["covenants.debtYieldStabilized"], 2) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "09697a6f-4a4a-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "effectiveRate",
    numeric: false,
    label: "Effective Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "effectiveRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "0bd583da-4a4a-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "averageProjectedRate",
    numeric: false,
    label: "Projected Average Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "averageProjectedRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "0ea85675-4a4a-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanType.name",
    numeric: false,
    label: "Quote Type",
    Cell: ({ record }) => (
      <TableCell>{record["loanType.name"] || "\u2014\u2014"}</TableCell>
    )
  },
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 999,
    numeric: false,
    Cell: ({ record }) => (
      <TableCell className="loansList_Menu" key="menu">
        <LoanTableOptionsMenu
          id={record.id}
          tableName={"RejectedLoanTableColumns"}
        />
      </TableCell>
    )
  }
];

export default RejectedLoanTableColumns;
